import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField, Chip, IconButton, Tooltip, Typography, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalShipping, Search, Refresh, CheckCircle, Cancel, Delete } from '@mui/icons-material';
import MainText from '../../components/MainText';
import CustomSwitch from '../../components/CustomSwitch';
import CleanningButton from '../../components/CleanningButton';
import { RBAC } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';
import CustomSnackBar from '../../components/CustomSnackBar';

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-419', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
};

export default function WarehouseList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const dispatch = useDispatch();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');

  useEffect(() => {
    dispatch(addTab({ label: 'Bodegas', path: '/catalogs/warehouses' }));
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilter = (name, value) => setFilters(v => ({ ...v, [name]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { active: filters.active ? 'true' : 'false' };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await RBAC.warehouses.list(params);
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setRows(data.map(w => ({ id: w.id, ...w })));
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.active]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (row) => {
    try {
      await RBAC.warehouses.update(row.id, { is_active: !row.is_active });
      load();
      setSnackMsg(row.is_active ? 'Bodega desactivada' : 'Bodega activada'); setSnackSev('success'); setSnackOpen(true);
    } catch {}
  };

  const removeRow = async (row) => {
    if (!window.confirm('¿Eliminar bodega permanentemente?')) return;
    try {
      await RBAC.warehouses.remove(row.id);
      load();
      setSnackMsg('Bodega eliminada'); setSnackSev('success'); setSnackOpen(true);
    } catch {}
  };

  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 80, renderCell: (p) => <Typography variant="caption" color="text.secondary">{p.value}</Typography> },
    { field: 'name', headerName: 'Bodega', flex: 1, minWidth: 240, renderCell: ({ value }) => <Typography sx={{ fontWeight: 700 }}>{value || '—'}</Typography> },
    { field: 'code', headerName: 'Código', width: 140 },
    { field: 'address', headerName: 'Dirección', width: 320, sortable: false, renderCell: ({ value }) => <Typography noWrap sx={{ width: '100%' }}>{value || '—'}</Typography> },
    { field: 'company', headerName: 'Empresa', width: 200, valueGetter: (p) => p.row?.company?.name || '—' },
    { field: 'is_active', headerName: 'Estado', width: 120, renderCell: ({ value }) => <Chip size="small" label={value ? 'Activa' : 'Inactiva'} color={value ? 'success' : 'default'} variant="outlined" /> },
    { field: 'actions', headerName: 'Acciones', width: 140, sortable: false, renderCell: ({ row }) => (
      <Stack direction="row" spacing={0.5}>
        {filters.active ? (
          <Tooltip title={row.is_active ? 'Desactivar' : 'Activar'}>
            <IconButton size="small" onClick={() => toggleActive(row)}>
              {row.is_active ? <Cancel fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Eliminar permanentemente">
            <IconButton size="small" color="error" onClick={() => removeRow(row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    ) },
    { field: 'createdAt', headerName: 'Creada', width: 180, renderCell: ({ value }) => <Typography variant="caption">{formatDateTime(value)}</Typography> },
  ]), [filters.active]);

  const clearAll = () => setFilters({ search: '', active: true });

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
        <MainText title="Bodegas" subtitle="Listado por empresa activa." icon={LocalShipping} width="auto" />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.search && (<Chip label={`Búsqueda: "${filters.search}"`} onDelete={() => handleFilter('search', '')} />)}
            {!filters.active && (<Chip label="Inactivas" color="error" onDelete={() => handleFilter('active', true)} />)}
            {!filters.search && filters.active && (<Chip label="Mostrando empresa actual" color="info" />)}
          </Box>
          <CleanningButton onClick={clearAll} />
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, código, dirección..."
          value={filters.search}
          onChange={(e) => handleFilter('search', e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
          sx={{ minWidth: { xs: '100%', md: 320 } }}
        />
        <CustomSwitch name="active" label={filters.active ? 'Activas' : 'Inactivas'} initialValue={filters.active} onChange={(_, val) => handleFilter('active', val)} />
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Refrescar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
        </Box>
      </Stack>

      <Box sx={{ height: 460, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={52}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
              backgroundColor: '#10173c !important',
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': { color: '#ffffff !important' },
          }}
        />
      </Box>
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
